import React from "react";
import { COLORS } from "./tokens";

/**
 * MAIN LOGO — Rona Wellness
 * Circular composition:
 *  - Dusty blue circular background
 *  - Thin gold outer ring
 *  - Soft yellow sun glow behind the tree
 *  - Minimal sage green tree (rounded organic canopy + slim trunk)
 *  - Natural roots flowing organically from the base
 *  - One central root flows FORWARD into a curving path (soft brown / gold-brown)
 *
 * Viewbox: 600 x 600 (exports to 2000 x 2000 and 4500 x 5400 transparent PNG).
 */
const MainLogo = React.forwardRef(function MainLogo(
  { size = 360, withRing = true, background = true, id = "rona-main-logo" },
  ref
) {
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 600 600"
      width={size}
      height={size}
      role="img"
      aria-label="Rona Wellness main logo"
    >
      <defs>
        <radialGradient id="ml-sun" cx="50%" cy="42%" r="42%">
          <stop offset="0%" stopColor={COLORS.yellowSoft} stopOpacity="1" />
          <stop offset="45%" stopColor={COLORS.yellow} stopOpacity="0.55" />
          <stop offset="100%" stopColor={COLORS.yellow} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ml-bg" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#9FB3C2" />
          <stop offset="100%" stopColor={COLORS.dustyBlue} />
        </radialGradient>
        <linearGradient id="ml-canopy" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#9EB3A1" />
          <stop offset="100%" stopColor={COLORS.sage} />
        </linearGradient>
        <linearGradient id="ml-path" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={COLORS.brown} />
          <stop offset="100%" stopColor={COLORS.goldDeep} />
        </linearGradient>
      </defs>

      {/* Background disk */}
      {background && (
        <circle cx="300" cy="300" r="282" fill="url(#ml-bg)" />
      )}

      {/* Soft sun glow */}
      <circle cx="300" cy="252" r="180" fill="url(#ml-sun)" />

      {/* Horizon hint — a very soft lighter arc behind tree */}
      <path
        d="M 90 380 Q 300 355 510 380"
        stroke={COLORS.creamSoft}
        strokeWidth="1.2"
        strokeOpacity="0.35"
        fill="none"
      />

      {/* Tree canopy — soft organic cluster of leaves */}
      <g>
        {/* big soft body */}
        <path
          d="M300 110
             C 235 112, 188 150, 182 200
             C 146 210, 128 250, 148 288
             C 128 318, 148 360, 196 360
             C 220 378, 260 380, 300 368
             C 340 380, 380 378, 404 360
             C 452 360, 472 318, 452 288
             C 472 250, 454 210, 418 200
             C 412 150, 365 112, 300 110 Z"
          fill="url(#ml-canopy)"
        />
        {/* subtle inner highlights */}
        <path
          d="M250 160 C 230 180, 220 210, 236 230"
          stroke={COLORS.creamSoft}
          strokeOpacity="0.18"
          strokeWidth="6"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M352 168 C 372 188, 378 220, 362 244"
          stroke={COLORS.creamSoft}
          strokeOpacity="0.14"
          strokeWidth="5"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* Trunk */}
      <path
        d="M292 360
           C 292 395, 288 415, 286 432
           L 314 432
           C 312 415, 308 395, 308 360 Z"
        fill={COLORS.brown}
      />

      {/* Roots — natural, organic, spreading outward. NO RW shape. */}
      <g
        stroke={COLORS.brown}
        strokeLinecap="round"
        fill="none"
      >
        {/* Left outer root */}
        <path d="M288 432 C 258 448, 222 452, 180 446" strokeWidth="7" />
        <path d="M220 446 C 198 454, 178 456, 156 452" strokeWidth="4" />
        {/* Left inner root */}
        <path d="M292 434 C 276 452, 256 468, 232 478" strokeWidth="6" />
        {/* Right outer root */}
        <path d="M312 432 C 342 448, 378 452, 420 446" strokeWidth="7" />
        <path d="M380 446 C 402 454, 422 456, 444 452" strokeWidth="4" />
        {/* Right inner root */}
        <path d="M308 434 C 324 452, 344 468, 368 478" strokeWidth="6" />
        {/* small feeder roots */}
        <path d="M198 452 C 188 460, 184 468, 186 476" strokeWidth="3" />
        <path d="M402 452 C 412 460, 416 468, 414 476" strokeWidth="3" />
      </g>

      {/* The PATH flowing forward from the roots — curving ribbon */}
      <path
        d="M300 434
           C 300 462, 282 480, 258 494
           C 226 512, 200 522, 168 528
           C 136 534, 118 540, 104 546"
        stroke="url(#ml-path)"
        strokeWidth="11"
        strokeLinecap="round"
        fill="none"
        opacity="0.95"
      />
      {/* twin path line for a subtle “road” feel */}
      <path
        d="M300 434
           C 302 462, 320 480, 344 494
           C 376 512, 402 522, 434 528
           C 466 534, 484 540, 498 546"
        stroke="url(#ml-path)"
        strokeWidth="11"
        strokeLinecap="round"
        fill="none"
        opacity="0.85"
      />

      {/* Outer thin gold ring */}
      {withRing && (
        <circle
          cx="300"
          cy="300"
          r="282"
          fill="none"
          stroke={COLORS.gold}
          strokeWidth="2.2"
        />
      )}
      {withRing && (
        <circle
          cx="300"
          cy="300"
          r="272"
          fill="none"
          stroke={COLORS.gold}
          strokeOpacity="0.35"
          strokeWidth="1"
        />
      )}
    </svg>
  );
});

export default MainLogo;
